import React from "react";
import { NavLink } from "react-router-dom";
import { useI18n } from "../../../lib/i18n";
import KpiCard from "../shared/KpiCard";
import { isBusinessVerified } from "../verificationStatus";
import { Eye, Heart, MessageSquare, LayoutGrid, TrendingUp, Users, Building2, Phone, MessageCircle, Star, ClipboardCheck, ArrowLeft, ShieldCheck, PlusCircle, Car } from "lucide-react";

/* Inventory categories — same set used by BusinessDashboardPage. Keeping
   it inline here (rather than importing) to avoid a new shared module
   just for one constant. */
const INVENTORY_CATEGORIES = new Set([
  "dealer", "official_agency", "rental_company", "auction_house", "salvage",
]);

export default function DashOverview({ home }) {
  const { lang } = useI18n();
  const verified = isBusinessVerified(home);
  const businessId = home?.business?.id;
  const k = home.kpis || {};
  const l = k.listings || {};
  const e = k.engagement || {};
  const rev = home.reviews || {};

  // Unverified accounts see a focused onboarding view instead of the
  // operational KPI dashboard. This prevents "empty stats confusion"
  // and channels attention to the verification journey.
  if (!verified) {
    return (
      <div className="space-y-6" data-testid="dash-overview-unverified">
        <div
          className="rounded-2xl p-8 sm:p-10 border"
          style={{
            background: "linear-gradient(135deg,#0a0a0a 0%,#1f1614 100%)",
            borderColor: "transparent",
          }}
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: "rgba(201,165,92,0.15)", border: "1px solid rgba(201,165,92,0.4)" }}
            >
              <ShieldCheck className="w-7 h-7" style={{ color: "#e8c873" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: "#e8c873" }}>
                {lang === "ar" ? "أهلاً بك في كرتال أعمال" : "Welcome to CARTAL Business"}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#f7eecd] mt-1">
                {lang === "ar" ? "أكمل توثيق نشاطك للبدء" : "Complete verification to get started"}
              </h2>
              <p className="text-[14px] text-[#f7eecd]/70 mt-3 leading-relaxed max-w-2xl">
                {lang === "ar"
                  ? "قبل موافقة إدارة كرتال على نشاطك، لن يتم فتح الوظائف التشغيلية مثل نشر المنتجات والخدمات، تفعيلها، أو ربط إعلانات المركبات باسم النشاط. أكمل بيانات النشاط، ارفع الوثائق المطلوبة، ثم أرسل الطلب للمراجعة."
                  : "Operational features (publishing products/services, activation, linking vehicle listings) unlock only after CARTAL admins approve your business. Complete the required data, upload documents, and submit for review."}
              </p>
              <NavLink
                to={`/business/${businessId}/verification`}
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[14px] font-bold"
                style={{ background: "linear-gradient(135deg,#c9a55c,#e8c873)", color: "#0a0a0a" }}
                data-testid="overview-goto-verification"
              >
                {lang === "ar" ? "استكمال التوثيق" : "Complete verification"}
                <ArrowLeft className="w-4 h-4" style={{ transform: lang === "ar" ? "scaleX(1)" : "scaleX(-1)" }} />
              </NavLink>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { ar: "نشر المنتجات والخدمات", en: "Publish products & services" },
            { ar: "استقبال طلبات العملاء",  en: "Receive customer requests" },
            { ar: "الظهور في الدليل العام",  en: "Appear in public directory" },
          ].map((item, i) => (
            <div
              key={i}
              className="rounded-xl bg-white border border-[#0a0a0a]/[0.06] p-4"
              data-testid={`unverified-feature-${i}`}
            >
              <div className="text-[11px] uppercase tracking-widest text-[#8a8a8a] font-semibold">
                {lang === "ar" ? "متاح بعد التوثيق" : "Unlocked after verification"}
              </div>
              <div className="text-[14px] font-bold text-[#0a0a0a] mt-1">
                {lang === "ar" ? item.ar : item.en}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dash-overview">
      {/* Dealer Listing Wizard shortcut — one-tap "أضف مركبة" that jumps
          straight to /sell with this business pre-selected. Only shown
          for inventory categories (dealer / agency / rental / auction /
          salvage) where car inventory is the primary product. */}
      {INVENTORY_CATEGORIES.has(home?.business?.category) && (
        <NavLink
          to={`/sell?business_id=${businessId}`}
          className="flex items-center justify-between gap-4 rounded-2xl p-5 border transition hover:translate-y-[-1px] hover:shadow-md"
          style={{
            background: "linear-gradient(120deg,#0a0a0a 0%,#1f1614 60%,#2a1f16 100%)",
            borderColor: "rgba(201,165,92,0.35)",
          }}
          data-testid="dealer-add-vehicle-shortcut"
        >
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                 style={{ background: "rgba(232,200,115,0.15)", border: "1px solid rgba(232,200,115,0.4)" }}>
              <Car className="w-6 h-6" style={{ color: "#e8c873" }} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: "#e8c873" }}>
                {lang === "ar" ? "إضافة سريعة" : "Quick add"}
              </div>
              <div className="text-lg sm:text-xl font-bold text-[#f7eecd] truncate">
                {lang === "ar" ? "أضف مركبة إلى مخزونك" : "Add a vehicle to your inventory"}
              </div>
              <div className="text-[12px] text-[#f7eecd]/70 mt-0.5">
                {lang === "ar"
                  ? "انتقل مباشرة إلى نموذج نشر إعلان جديد لهذا النشاط."
                  : "Jump straight to the new-listing form for this business."}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-bold shrink-0"
               style={{ background: "linear-gradient(135deg,#c9a55c,#e8c873)", color: "#0a0a0a" }}>
            <PlusCircle className="w-4 h-4" />
            <span className="hidden sm:inline">{lang === "ar" ? "أضف مركبة" : "Add vehicle"}</span>
          </div>
        </NavLink>
      )}

      {/* Top KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testid="kpi-listings" icon={LayoutGrid} labelAr="إعلانات نشطة" labelEn="Active listings"
                 value={l.active} sub={`${l.total || 0} ${lang === "ar" ? "إجمالي" : "total"}`} />
        <KpiCard testid="kpi-views" icon={Eye} labelAr="مشاهدات" labelEn="Views"
                 value={e.views_total} tone="accent" />
        <KpiCard testid="kpi-favorites" icon={Heart} labelAr="حفظ للمفضلة" labelEn="Favorites"
                 value={e.favorites_total} />
        <KpiCard testid="kpi-messages" icon={MessageSquare} labelAr="الرسائل" labelEn="Messages"
                 value={e.messages_total} />
      </div>

      {/* Conversion strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testid="kpi-phone" icon={Phone} labelAr="نقرات الهاتف (30 يوم)" labelEn="Phone clicks (30d)"
                 value={e.phone_clicks_30d} tone="soft" />
        <KpiCard testid="kpi-whatsapp" icon={MessageCircle} labelAr="نقرات واتساب" labelEn="WhatsApp clicks"
                 value={e.whatsapp_clicks_30d} tone="soft" />
        <KpiCard testid="kpi-leads" icon={TrendingUp} labelAr="عملاء محتملون" labelEn="Leads (30d)"
                 value={e.leads_30d} tone="soft" />
        <KpiCard testid="kpi-featured" icon={Star} labelAr="إعلانات مميزة" labelEn="Featured"
                 value={l.featured} tone="soft" />
      </div>

      {/* Two-col cards row */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5" data-testid="dash-listings-breakdown">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-3">
            {lang === "ar" ? "توزيع الإعلانات" : "Listings breakdown"}
          </div>
          <div className="grid grid-cols-2 gap-y-3 text-sm">
            {[
              ["active", "نشط", "Active"], ["draft", "مسودّة", "Draft"],
              ["sold", "مُباع", "Sold"], ["expired", "منتهي", "Expired"],
              ["rejected", "مرفوض", "Rejected"], ["archived", "مؤرشف", "Archived"],
            ].map(([key, ar, en]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-[#525252]">{lang === "ar" ? ar : en}</span>
                <span className="font-bold text-[#0a0a0a]">{l[key] ?? 0}</span>
              </div>
            ))}
          </div>
        </div>

        {/* iter173 — hide the Organization card entirely for personal
            (individual) activities; those visitors get a simple
            Verification-only summary instead.
            iter174 — Verification status is now localized via
            _verificationLabel; users never see raw English keys like
            "verified" / "unverified" / "pending". */}
        {(() => {
          const _verificationLabel = (s) => {
            const key = (s || "unverified").toString().toLowerCase();
            const map = {
              verified:   { ar: "موثّق",         en: "Verified" },
              approved:   { ar: "موثّق",         en: "Verified" },
              pending:    { ar: "قيد المراجعة",  en: "Pending review" },
              in_review:  { ar: "قيد المراجعة",  en: "Pending review" },
              submitted:  { ar: "قيد المراجعة",  en: "Pending review" },
              rejected:   { ar: "مرفوض",         en: "Rejected" },
              suspended:  { ar: "موقوف",         en: "Suspended" },
              unverified: { ar: "غير موثّق",     en: "Unverified" },
            };
            const entry = map[key] || map.unverified;
            return entry[lang] || entry.en;
          };
          const vStatus = _verificationLabel(home.verification?.business_verification_status);
          return home.business?.category === "individual" ? (
            <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5" data-testid="dash-individual-snapshot">
              <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-3">
                {lang === "ar" ? "حسابي" : "My account"}
              </div>
              <div className="grid grid-cols-1 gap-y-3 text-sm">
                <Row icon={ClipboardCheck} labelAr="حالة التوثيق" labelEn="Verification" value={vStatus} />
                <Row icon={Star} labelAr="متوسط التقييم" labelEn="Avg rating" value={rev.average ?? "—"} />
              </div>
            </div>
          ) : (
            <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5" data-testid="dash-org-snapshot">
              <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-3">
                {lang === "ar" ? "المنظمة" : "Organization"}
              </div>
              <div className="grid grid-cols-2 gap-y-3 text-sm">
                <Row icon={Building2} labelAr="فروع" labelEn="Branches" value={home.branches_count} />
                <Row icon={Users} labelAr="موظفون" labelEn="Employees" value={home.employees_count} />
                <Row icon={Star} labelAr="متوسط التقييم" labelEn="Avg rating" value={rev.average ?? "—"} />
                <Row icon={ClipboardCheck} labelAr="حالة التوثيق" labelEn="Verification" value={vStatus} />
              </div>
            </div>
          );
        })()}
      </div>

      {/* Top listings */}
      {home.top_listings?.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5" data-testid="dash-top-listings">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e]">
              {lang === "ar" ? "الإعلانات الأعلى أداءً" : "Top-performing listings"}
            </div>
            <div className="text-[11px] text-[#8a8a8a]">{lang === "ar" ? "حسب المشاهدات" : "by views"}</div>
          </div>
          <div className="divide-y divide-[#0a0a0a]/[0.06]">
            {home.top_listings.map((v) => (
              <a key={v.id} href={`/vehicle/${v.id}`} className="flex items-center gap-3 py-3 hover:bg-[#fafaf7] rounded-lg px-2 -mx-2 transition-colors">
                <div className="w-14 h-10 rounded-lg bg-[#f0eee7] overflow-hidden shrink-0">
                  {v.images?.[0] && <img src={v.images[0]} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#0a0a0a] truncate">
                    {v.title || `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim()}
                  </div>
                  <div className="text-[11px] text-[#8a8a8a]">{v.status} · {v.listing_type}</div>
                </div>
                <div className="text-end shrink-0">
                  <div className="text-[13px] font-bold text-[#0a0a0a]">{(v.view_count || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-[#8a8a8a]">{lang === "ar" ? "مشاهدة" : "views"}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, labelAr, labelEn, value }) {
  const { lang } = useI18n();
  return (
    <>
      <span className="text-[#525252] inline-flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" /> {lang === "ar" ? labelAr : labelEn}
      </span>
      <span className="font-bold text-[#0a0a0a] text-end">{value ?? 0}</span>
    </>
  );
}
